import { useState } from "react";
import { toast } from "sonner";
import { Radar } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CATEGORIES, PLATFORMS, US_STATES } from "../lib/constants";
import { api, formatApiError } from "../lib/api";

const Chips = ({ items, selected, toggle, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((i) => {
      const on = selected.includes(i.value);
      return (
        <button type="button" key={i.value} onClick={() => toggle(i.value)} data-testid={`${prefix}-${i.value}`}
          className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors duration-150 ${on ? "bg-blue-700 border-blue-700 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"}`}>
          {i.label}
        </button>
      );
    })}
  </div>
);

export const ScanDialog = ({ open, setOpen, onStarted, allowedStates = [] }) => {
  const [cats, setCats] = useState(CATEGORIES.map((c) => c.value));
  const [plats, setPlats] = useState(PLATFORMS.map((p) => p.value));
  const [state, setState] = useState(allowedStates[0] || "all");
  const [busy, setBusy] = useState(false);
  const tog = (setter) => (v) => setter((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/scans", { categories: cats, platforms: plats, state: state === "all" ? null : state });
      toast.success("Scan started — results appear in 1–2 minutes");
      onStarted(data);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold">New web scan</DialogTitle>
          <DialogDescription>AI searches public posts and pages for businesses wanting to replace their POS.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div>
            <p className="font-mono-label text-[10px] text-slate-500 mb-2">State</p>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger className="rounded-full" data-testid="scan-state-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {!allowedStates.length && <SelectItem value="all">All US states</SelectItem>}
                {Object.entries(US_STATES).filter(([k]) => !allowedStates.length || allowedStates.includes(k))
                  .map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-slate-500 mb-2">Categories</p>
            <Chips items={CATEGORIES} selected={cats} toggle={tog(setCats)} prefix="scan-category" />
          </div>
          <div>
            <p className="font-mono-label text-[10px] text-slate-500 mb-2">Sources</p>
            <Chips items={PLATFORMS} selected={plats} toggle={tog(setPlats)} prefix="scan-platform" />
          </div>
          <Button onClick={start} disabled={busy || !cats.length || !plats.length} className="w-full h-11 rounded-full bg-blue-700 hover:bg-blue-800" data-testid="start-scan-submit">
            <Radar className="h-4 w-4 mr-2" />{busy ? "Starting…" : "Start scan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
